import DropdownCtaController from "./dropdown_cta_controller.js"

// Reuse the shared popover's positioning and keyboard
// behavior; measure the real trail after its sibling actions have taken space.
export default class extends DropdownCtaController {
  static targets = ["list", "middle", "overflow"]

  connect() {
    if (this.hasMenuTarget && !this.menuTarget.showPopover) return
    this.element.setAttribute("data-overflow-ready", "")
    if (!this.hasMenuTarget) return
    super.connect()
    this.refresh = () => {
      cancelAnimationFrame(this.fitFrame)
      this.fitFrame = requestAnimationFrame(() => this.fit())
    }
    this.onCache = () => this.close(false)
    this.onScroll = event => {
      if (!this.menuTarget.contains(event.target)) this.close(false)
    }
    // Only width matters for fitting. Ignoring height-only changes keeps the
    // parent observer below from refitting (and closing the menu) whenever the
    // surrounding page grows.
    this.widths = new WeakMap()
    this.observer = new ResizeObserver(entries => {
      // Record every entry's width before deciding; short-circuiting would
      // leave a stale width that misfires on the next height-only change.
      const widthChanged = (entries ?? []).reduce((changed, { target, contentRect }) => {
        const previous = this.widths.get(target)
        this.widths.set(target, contentRect.width)
        return previous !== contentRect.width || changed
      }, !entries)
      if (widthChanged) this.refresh()
    })
    this.observer.observe(this.element)
    // A content-sized trail doesn't resize itself when room opens up again, so
    // also watch the container it lives in to know when to re-expand.
    if (this.element.parentElement) this.observer.observe(this.element.parentElement)
    document.fonts?.addEventListener("loadingdone", this.refresh)
    document.addEventListener("turbo:before-cache", this.onCache)
    document.addEventListener("scroll", this.onScroll, true)
    this.fit()
  }

  disconnect() {
    if (!this.observer) return
    this.close(false)
    document.removeEventListener("keydown", this._onKeydown)
    this.observer?.disconnect()
    cancelAnimationFrame(this.fitFrame)
    clearTimeout(this.leaveTimer)
    document.fonts?.removeEventListener("loadingdone", this.refresh)
    document.removeEventListener("turbo:before-cache", this.onCache)
    document.removeEventListener("scroll", this.onScroll, true)
    super.disconnect()
  }

  fit() {
    if (!this.element.clientWidth) return
    const active = document.activeElement
    const focusedMiddle = this.middleTargets.some(item => item.contains(active))
    const focusedOverflow = this.overflowTarget.contains(active)
    const focusedMenu = this.menuTarget.contains(active)
    this.close(false)
    // Measure the expanded content synchronously, then restore the final state
    // before painting. This also lets the trail expand again after resizing.
    this.element.dataset.measuring = ""
    this.middleTargets.forEach(item => { item.hidden = false })
    this.overflowTarget.hidden = true
    const collapsed = this.listTarget.scrollWidth > this.listTarget.clientWidth + 1
    this.middleTargets.forEach(item => { item.hidden = collapsed })
    this.overflowTarget.hidden = !collapsed
    this.element.toggleAttribute("data-collapsed", collapsed)
    delete this.element.dataset.measuring
    if (collapsed && (focusedMiddle || focusedMenu)) this.triggerTarget.focus({ preventScroll: true })
    if (!collapsed && (focusedOverflow || focusedMenu)) this.middleTargets[0].querySelector("a").focus({ preventScroll: true })
  }

  open() {
    if (this.overflowTarget.hidden || this._isOpen()) return
    super.open()
    // Keep the shared flip positioning, then inset the rendered menu from the
    // viewport edges. Its CSS max dimensions leave room for this gutter.
    const menu = this.menuTarget
    const rect = menu.getBoundingClientRect()
    const gutter = 12
    menu.style.left = `${Math.max(gutter, Math.min(rect.left, window.innerWidth - rect.width - gutter))}px`
    menu.style.right = "auto"
    menu.style.top = `${Math.max(gutter, Math.min(rect.top, window.innerHeight - rect.height - gutter))}px`
    this._setFocus(0)
  }

  close(restoreFocus = true) {
    if (!this.hasMenuTarget || !this._isOpen()) return
    this.menuTarget.hidePopover()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    document.removeEventListener("keydown", this._onKeydown)
    this._clearFocus()
    if (restoreFocus) this.triggerTarget.focus({ preventScroll: true })
  }

  openWithKeyboard(event) {
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return
    event.preventDefault()
    event.stopPropagation()
    this.open()
    if (event.key === "ArrowUp") this._setFocus(this.itemTargets.length - 1)
  }

  leave() {
    clearTimeout(this.leaveTimer)
    this.leaveTimer = setTimeout(() => {
      if (!this.element.contains(document.activeElement)) this.close(false)
    }, 0)
  }
}
